import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@parrot-software/design-system';
import { TriangleAlert } from '@parrot-software/design-system/icons';
import { useIsMobile } from '@parrot-software/hooks';
import type { ConceptLabels, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { mapStoresToConcepts } from '@subscriptions/application/mappers';
import { CustomerStates, SubscriptionStatuses } from '@subscriptions/domain/types/SubscriptionsConst';
import {
  InactiveStatusBadge,
  LicenseStatusBadge,
  PaymentAlertBadge,
  SubscriptionIntervalBadge,
} from '@subscriptions/presentation/components/base';
import {
  formatCurrencyMXN,
  formatPaymentDate,
  getLicenseBadgeVariant,
  isPromoBlock,
} from '@subscriptions/presentation/components/specific/SubscriptionsList/utils';
import {
  getActiveDiscountTotal,
  getNextBillingDateLabel,
  getPendingDiscountDateLabel,
  hasPendingDiscount,
} from '@subscriptions/presentation/utils';
import { BlockConceptsCards } from './BlockConceptsCards';
import { BlockConceptsTable } from './BlockConceptsTable';

interface BlockChargeCardProps {
  block: SubscriptionBlockViewDto;
}

/**
 * Next charge of the subscription: date, total and the concepts that make it up. A churned block
 * shows instead what reactivating costs today and what it will be billed once back.
 *
 * The headline shows the total the API returns: it already applies the discounts that the
 * concepts below never carry, so summing those rows would overcharge the block.
 */
export const BlockChargeCard = ({ block }: BlockChargeCardProps) => {
  const { t } = useTranslation('subscriptions');
  const isMobile = useIsMobile();

  const isPaymentRequired = block.status === SubscriptionStatuses.PAYMENT_REQUIRED;
  const isInactive = block.customerState === CustomerStates.INACTIVE;
  const licenseBadge = getLicenseBadgeVariant(block, 'detail');
  const storeCount = block.stores.length;

  const labels: ConceptLabels = useMemo(
    () => ({
      conceptName: (key) => t(`blockDetail.summary.concepts.names.${key}`, { defaultValue: '' }) || null,
      planName: (plan) => t('blockDetail.summary.concepts.planName', { plan }),
      coversPeriod: (range) => t('blockDetail.summary.concepts.covers', { range }),
      proratedPeriod: (range, days, cycleDays) =>
        t('blockDetail.summary.concepts.proratedPeriod', { range, days, cycleDays }),
      emptyQuantity: t('blockDetail.summary.concepts.emptyQuantity'),
    }),
    [t],
  );

  const concepts = useMemo(() => mapStoresToConcepts(block.stores, labels, isInactive), [
    block.stores,
    labels,
    isInactive,
  ]);

  const chargeTitle = isInactive
    ? t('blockDetail.summary.reactivationCharge', {
        store: storeCount === 1 ? block.stores[0].storeName : block.name,
      })
    : t('blockDetail.summary.nextCharge', { date: formatPaymentDate(block.paymentDate) });

  const conceptCountLabel =
    concepts.conceptCount === 1
      ? t('blockDetail.summary.concepts.conceptCountOne')
      : t('blockDetail.summary.concepts.conceptCountOther', { count: concepts.conceptCount });

  const storeCountLabel =
    storeCount === 1
      ? t('blockDetail.summary.storeCountOne')
      : t('blockDetail.summary.storeCountOther', { count: storeCount });

  const chargeScope = isInactive ? conceptCountLabel : storeCountLabel;

  const pendingDiscountDate = !isInactive && hasPendingDiscount(block, concepts) ? getPendingDiscountDateLabel() : null;

  const nextBillingDate = getNextBillingDateLabel(block.paymentDate);
  const breakdownNote =
    isInactive && concepts.fullCycleTotalLabel
      ? t(
          nextBillingDate
            ? 'blockDetail.summary.churnedBreakdownNote'
            : 'blockDetail.summary.churnedBreakdownNoteWithoutDate',
          {
            amount: concepts.fullCycleTotalLabel,
            date: nextBillingDate,
          },
        )
      : t('blockDetail.summary.breakdownNote');

  return (
    <Card className="overflow-hidden p-0 font-sans shadow-xs">
      <div className="border-b border-border p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 text-base font-semibold tracking-tight">{chargeTitle}</p>
          {isInactive ? (
            <InactiveStatusBadge />
          ) : (
            <>
              {licenseBadge && <LicenseStatusBadge variant={licenseBadge} />}
              {isPaymentRequired && <PaymentAlertBadge />}
            </>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-baseline gap-2.5">
          <span className="text-[2.125rem] font-bold leading-none tracking-tight tabular-nums">
            {formatCurrencyMXN(block.total)}
          </span>
          <span className="inline-flex flex-wrap items-center gap-2">
            <SubscriptionIntervalBadge isOneOff={isInactive} isPromo={isPromoBlock(block)} />
            <span className="text-sm text-muted-foreground">
              {chargeScope} · {t('blockDetail.summary.taxIncluded')}
            </span>
          </span>
        </div>

        {pendingDiscountDate && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-bg-warning p-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-sm text-foreground">
              {t('blockDetail.summary.pendingDiscount', {
                amount: formatCurrencyMXN(getActiveDiscountTotal(block)),
                date: pendingDiscountDate,
              })}
            </p>
          </div>
        )}
      </div>

      <div className="p-5">
        <p className="text-sm font-semibold">{t('blockDetail.summary.breakdownTitle')}</p>

        <div className="mt-3">
          {isMobile ? <BlockConceptsCards concepts={concepts} /> : <BlockConceptsTable concepts={concepts} />}
        </div>

        <p className="mt-3 text-xs text-muted-foreground">{breakdownNote}</p>
      </div>
    </Card>
  );
};
